import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "react-bootstrap";
import { EconomicCard } from "../../components/economic-card.component"
import { UIInput } from "../../components/ui-input";
import { socialLogins } from "../../constant/sign-up.constant";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getItem, getLocalizedPathFromLocalStorage, setItem } from "../../helper/localstorage.helper";
import { signInWithEmailAndPassword, reset } from "../../features/signin/signin.slice";
import { AppDispatch, RootState } from "../../store";
import BankIdLogin from "../bankId-login/bankId-signin";
import ReactivateModal from "../../components/reactivatemodal/reactivate.modal";
import { setToggleReactivate, setInactiveAccount } from "../../features/bankid/bankid.slice";
import { selectToggleReactivate, selectInactiveAccount } from "../../features/bankid/bankid.selectors";
import { handleSocialLoginAction, handleFormValidation, validateEmail, validatePassword } from "../../utils/registration-validation";
import { useSigninSuccess, useSigninError } from "../../hooks/useSignin";
import { RegistrationHeader } from "../../components/registeration-header-.component";
import "../../scss/registration/registration.scss";
import { toast } from "react-toastify";

export const SignIn: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { loading: isSubmitting, error: submitError, isSuccess, user } = useSelector((state: RootState) => state.signin);
    const toggleReactivate = useSelector(selectToggleReactivate);
    const inactiveAccount = useSelector(selectInactiveAccount);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [showBankIDModal, setShowBankIDModal] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    const [show, setShow] = useState(false);
    const [searchParams] = useSearchParams();
    const [visibility, setVisibility] = useState(false);

    useSigninError(submitError);
    useSigninSuccess(isSuccess, user);

    useEffect(() => {
        dispatch(reset());
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);

    useEffect(() => {

        const message = searchParams.get("message");
        if (message) {
            const splitMessage = message.split("?")[0];

            if (splitMessage === "account_deactivated") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(false));
                return;
            }

            if (splitMessage === "inactive_account") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(true));
                return;
            }

            toast.success(t(message) as string);
            const lang = searchParams.get('lang') || 'en-us';
            // @ts-ignore
            const preferredVersion = user;
            setItem(
                'currentappversion',
                preferredVersion === 1 ? 'v1' : preferredVersion === 2 ? 'v2' : 'v2'
            );

            navigate(
                preferredVersion === 2
                    ? `/v2/dashboard?lang=${lang}`
                    : lang === 'en-us'
                        ? `/dashboard/activities?lang=${lang}`
                        : `/instrumentbrada/aktiviteter?lang=${lang}`
            );
        }

        setShow(false);
    }, [searchParams, t, dispatch, navigate]);

    useEffect(() => {
        if (toggleReactivate) {
            setShowBankIDModal(false);
        }
    }, [toggleReactivate]);

    const handleInputChange = (field: 'email' | 'password', value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        const newErrors = { ...errors };

        if (field === 'email') {
            if (!value.trim()) {
                newErrors.email = t("EMAIL_REQUIRED") as string;
            } else if (!validateEmail(value)) {
                newErrors.email = t("EMAIL_INVALID") as string;
            } else {
                newErrors.email = '';
            }
        }

        if (field === 'password') {
            if (!value) {
                newErrors.password = t("PASSWORD_REQUIRED") as string;
            } else {
                newErrors.password = '';
            }
        }

        setErrors(newErrors);
    };

    const handleSocialLogin = (key: string) => {
        handleSocialLoginAction(key, setShowBankIDModal);
    };

    const handleReactivateClose = () => {
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    };

    const handleReactivateSuccess = () => {
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    };
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!handleFormValidation(formData, t, setErrors)) {
            return;
        }

        setLoading(true);
        try {
            const { payload } = await dispatch(signInWithEmailAndPassword({
                email: formData.email,
                password: formData.password,
            }));
            if (payload === "account_deactivated") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(false));
                return;
            }

            if (payload === "inactive_account") {
                dispatch(setToggleReactivate(true));
                dispatch(setInactiveAccount(true));
                return;
            }
            // @ts-ignore
            if (!payload?.token) return;
            const lang = searchParams.get('lang') || 'en-us';
            // @ts-ignore
            const preferredVersion = payload.userData?.preferredPlatformVersion;
            setItem(
                'currentappversion',
                preferredVersion === 1 ? 'v1' : preferredVersion === 2 ? 'v2' : 'v2'
            );

            navigate(
                preferredVersion === 2
                    ? `/v2/dashboard?lang=${lang}`
                    : lang === 'en-us'
                        ? `/dashboard/activities?lang=${lang}`
                        : `/instrumentbrada/aktiviteter?lang=${lang}`
            );

            return payload;
        } catch (error) {
            console.error("Sign in error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-page-wrapper px-0 ">
            <RegistrationHeader />
            <Container className="pt-4">
                <Row className="px-0">
                    <Col sm={12} md={6} className="px-0 pr-3 d-sm-flex justify-content-sm-start align-items-sm-center">
                        <div className="px-0">
                            <h1 className="registration-title">{t("LOGIN_TITLE")}</h1>
                            <div >

                                <form className="registration-form d-flex jutify-content-center" onSubmit={onSubmit}>

                                    {/* Email Field */}
                                    <UIInput
                                        type="email"
                                        label={t("REGISTRATION_EMAIL_LABEL")}
                                        placeholder={t("REGISTRATION_EMAIL_PLACEHOLDER")}
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        error={!!errors.email}
                                        errorMessage={errors.email}
                                        required={true}
                                    />

                                    {/* Password Field */}
                                    <UIInput
                                        type={visibility ? "text" : "password"}
                                        label={t("REGISTRATION_PASSWORD_LABEL")}
                                        placeholder={t("REGISTRATION_PASSWORD_PLACEHOLDER")}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        error={!!errors.password}
                                        errorMessage={errors.password}
                                        required={true}
                                        icon={
                                            <button
                                                className="btn btn-link p-0 m-0"
                                                type="button"
                                                aria-label={show ? t("HIDE_PASSWORD") : t("SHOW_PASSWORD")}
                                                title={show ? t("HIDE_PASSWORD") : t("SHOW_PASSWORD")}
                                                tabIndex={0}
                                                style={{ pointerEvents: 'auto', zIndex: 2 }}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setVisibility(s => !s);
                                                }}
                                            >
                                                {!visibility ? <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={18}
                                                    height={14}
                                                    viewBox="0 0 18 14"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1.42442 7.97582C1.13978 7.35655 1.13978 6.64214 1.42442 6.02288C2.742 3.15637 5.63866 1.16602 9.00019 1.16602C12.3617 1.16602 15.2584 3.15637 16.576 6.02288C16.8606 6.64215 16.8606 7.35656 16.576 7.97582C15.2584 10.8423 12.3617 12.8327 9.00019 12.8327C5.63866 12.8327 2.74201 10.8423 1.42442 7.97582Z"
                                                        stroke="black"
                                                        strokeWidth={2}
                                                    />
                                                    <path
                                                        d="M11.5002 6.99935C11.5002 8.38006 10.3809 9.49935 9.00019 9.49935C7.61948 9.49935 6.50019 8.38006 6.50019 6.99935C6.50019 5.61864 7.61948 4.49935 9.00019 4.49935C10.3809 4.49935 11.5002 5.61864 11.5002 6.99935Z"
                                                        stroke="black"
                                                        strokeWidth={2}
                                                    />
                                                </svg> : <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={22}
                                                    height={18}
                                                    viewBox="0 0 22 18"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1.9095 7.82824L2.81811 8.24589L2.81811 8.24588L1.9095 7.82824ZM20.0913 10.1718L19.1827 9.75412L20.0913 10.1718ZM1.9095 10.1718L1.00089 10.5894H1.00089L1.9095 10.1718ZM20.0913 7.82824L19.1827 8.24588V8.24588L20.0913 7.82824ZM16.9678 2.75789C16.5041 2.45792 15.885 2.59066 15.585 3.05438C15.285 3.5181 15.4178 4.13719 15.8815 4.43716L16.9678 2.75789ZM6.1616 13.59C5.69622 13.2926 5.07787 13.4288 4.78049 13.8942C4.48311 14.3596 4.6193 14.9779 5.08469 15.2753L6.1616 13.59ZM2.20093 12.6017C2.53267 13.0432 3.15955 13.1322 3.6011 12.8005C4.04265 12.4687 4.13166 11.8418 3.79992 11.4003L2.20093 12.6017ZM12.8015 3.18005C13.3427 3.28992 13.8706 2.94022 13.9804 2.39897C14.0903 1.85772 13.7406 1.32989 13.1994 1.22002L12.8015 3.18005ZM19.7075 1.70711C20.098 1.31658 20.098 0.683418 19.7075 0.292893C19.317 -0.0976311 18.6838 -0.0976311 18.2933 0.292893L19.7075 1.70711ZM2.2933 16.2929C1.90278 16.6834 1.90278 17.3166 2.2933 17.7071C2.68383 18.0976 3.31699 18.0976 3.70752 17.7071L2.2933 16.2929ZM11.0004 1C6.56168 1 2.73907 3.62905 1.00089 7.4106L2.81811 8.24588C4.24213 5.14781 7.3715 3 11.0004 3V1ZM11.0004 17C15.4392 17 19.2618 14.371 21 10.5894L19.1827 9.75412C17.7587 12.8522 14.6293 15 11.0004 15V17ZM1.00089 7.4106C0.537465 8.41881 0.537464 9.5812 1.00089 10.5894L2.81811 9.75412C2.59839 9.2761 2.59839 8.7239 2.81811 8.24589L1.00089 7.4106ZM19.1827 8.24588C19.4025 8.7239 19.4025 9.2761 19.1827 9.75412L21 10.5894C21.4634 9.58119 21.4634 8.41881 21 7.4106L19.1827 8.24588ZM21 7.4106C20.1226 5.50182 18.7158 3.88861 16.9678 2.75789L15.8815 4.43716C17.3133 5.36336 18.4653 6.68497 19.1827 8.24588L21 7.4106ZM5.08469 15.2753C6.79347 16.3672 8.82458 17 11.0004 17V15C9.21745 15 7.55838 14.4826 6.1616 13.59L5.08469 15.2753ZM1.00089 10.5894C1.32986 11.3051 1.73322 11.9791 2.20093 12.6017L3.79992 11.4003C3.41693 10.8905 3.08699 10.3391 2.81811 9.75412L1.00089 10.5894ZM13.1994 1.22002C12.488 1.07563 11.7525 1 11.0004 1V3C11.6182 3 12.2204 3.06209 12.8015 3.18005L13.1994 1.22002ZM8.17199 11.8284C9.73409 13.3905 12.2667 13.3905 13.8288 11.8284L12.4146 10.4142C11.6336 11.1953 10.3673 11.1953 9.58621 10.4142L8.17199 11.8284ZM13.8288 11.8284C15.3909 10.2663 15.3909 7.73367 13.8288 6.17157L12.4146 7.58579C13.1957 8.36684 13.1957 9.63317 12.4146 10.4142L13.8288 11.8284ZM18.2933 0.292893L2.2933 16.2929L3.70752 17.7071L19.7075 1.70711L18.2933 0.292893Z"
                                                        fill="black"
                                                    />
                                                </svg>
                                                }

                                            </button>
                                        }
                                    />


                                    <div className="flex-row d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex justify-content-center flex-row align-items-center">
                                            <div className="checkbox-wrapper">
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox-input"
                                                        checked={formData.rememberMe}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            rememberMe: e.target.checked
                                                        }))}
                                                    />
                                                    <span className="checkbox-checkmark"></span>
                                                    <span className="checkbox-text remember-me">{t("REMEMBER_ME")}</span>
                                                </label>
                                            </div>
                                        </div>

                                        <Link to={getLocalizedPathFromLocalStorage(
                                            { get: (key: string) => getItem(key) ?? null },
                                            { 'sv-se': 'glomt-ditt-losenord', 'en-us': 'forgot-password' }
                                        )} className="forgot-password-link">
                                            {t("FORGOT_PASSWORD")}
                                        </Link>
                                    </div>


                                    <button type="submit" className="submit-btn" disabled={loading || isSubmitting}>
                                        {(loading || isSubmitting) ? t("SIGN_IN_LOADING") : t("SIGN_IN_BUTTON")}
                                    </button>
                                </form>

                                <div className="social-login-section">
                                    <div className="divider">
                                        <span>{t("REGISTRATION_OR_SIGN_IN_WITH")}</span>
                                    </div>

                                    <div className="social-buttons ">
                                        {socialLogins.map(({ key, icon, alt, label }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className="social-btn d-flex jutify-content-center align-items-center"
                                                onClick={() => handleSocialLogin(key)}
                                                disabled={loading || isSubmitting}
                                            >
                                                <img src={icon} alt={alt} />
                                                <span className="social-btn-text">{t(label)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="registration-footer-text">
                                    <span>{t("REGISTRATION_DONT_HAVE_ACCOUNT")}</span>
                                    <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postregister', 'en-us': 'emailregister' })} className="sign-up-link-btn">{t("SIGN_UP_BUTTON")}</Link>
                                </div>
                                <div className="position-absolute mx-auto d-none d-sm-block" style={{
                                    bottom: '40px',
                                    left: "120px"
                                }}>
                                    <div className="trustpilot-widget" data-locale="sv-SE" data-template-id="5419b732fbfb950b10de65e5" data-businessunit-id="64f712e3dbd20bbfcf3a825f" data-style-height="24px" data-style-width="100%">
                                        <a href="https://se.trustpilot.com/review/invozio.com" target="_blank" rel="noopener">Trustpilot</a>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </Col>
                    <Col sm={12} md={6} className="px-0 d-none d-md-block">
                        <EconomicCard />
                    </Col>
                </Row>
            </Container>
            {showBankIDModal && <BankIdLogin onClose={() => setShowBankIDModal(false)} />}
            <ReactivateModal
                show={toggleReactivate}
                onClose={handleReactivateClose}
                onSuccess={handleReactivateSuccess}
                email={formData.email}
                getParamsEmail={searchParams.get("email") || undefined}
                inactiveAccount={inactiveAccount}
                mode="login"
            />
        </div>
    );
};

export default SignIn;
